// src/pages/customer/WishlistPage.jsx
import React, { useEffect, useState, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useNavigate } from "react-router-dom";
import { FiHeart, FiShoppingCart, FiArrowLeft, FiMoreVertical, FiTrash2 } from "react-icons/fi";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import { useAuth } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import { getWishlist, removeFromWishlist } from "../../services/firestoreService";
import toast from "react-hot-toast";

function StarRating({ rating = 4.8, reviews = 120 }) {
  const full  = Math.floor(rating);
  const half  = rating % 1 >= 0.5 ? 1 : 0;
  const empty = 5 - full - half;
  return (
    <div className="flex items-center justify-center gap-1.5 flex-wrap">
      <div className="flex items-center gap-0.5">
        {[...Array(full)].map((_, i)  => <FaStar        key={`f${i}`} className="text-amber-400 text-xs sm:text-sm" />)}
        {half === 1                    && <FaStarHalfAlt              className="text-amber-400 text-xs sm:text-sm" />}
        {[...Array(empty)].map((_, i) => <FaRegStar      key={`e${i}`} className="text-amber-400 text-xs sm:text-sm" />)}
      </div>
      <span className="text-[10px] sm:text-xs text-gray-500">{reviews} reviews</span>
    </div>
  );
}

export default function WishlistPage() {
  const { user } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    getWishlist(user.uid)
      .then(setItems)
      .finally(() => setLoading(false));
  }, [user]);

  const handleRemove = async (e, productId, name) => {
    e.preventDefault();
    await removeFromWishlist(user.uid, productId);
    setItems((prev) => prev.filter((i) => i.productId !== productId));
    toast.success(`${name} removed from wishlist`);
  };

  const handleMoveToCart = (e, item) => {
    e.preventDefault();
    addItem({
      id: item.productId,
      name: item.name,
      price: item.price,
      image: item.image,
      unit: item.unit,
      weightSize: item.weightSize,
      packSize: item.packSize,
      pricePerKg: item.pricePerKg,
      pricePerPack: item.pricePerPack,
    });
    toast.success(`${item.name} added to cart`);
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-green-700 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-6 sm:py-10">
      <Helmet>
        <title>My Wishlist | Tripura Agro</title>
        <meta
          name="description"
          content="Tripura Agro Industries was established in 2022 with the goal of leveraging supply and demand between farmers and Mandis at the national level and the final customer, who may be an individual or an institution."
        />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-black/5">
          <FiArrowLeft className="w-5 h-5 text-gray-600" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2" style={{ fontFamily: "Georgia, serif" }}>
          <FiHeart className="text-red-500 fill-red-500" /> My Wishlist
          <span className="text-base font-normal text-gray-400">({items.length} items)</span>
        </h1>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-24">
          <div className="text-7xl mb-4">🤍</div>
          <p className="text-gray-500 text-lg mb-6">Your wishlist is empty</p>
          <Link
            to="/products"
            className="inline-flex items-center gap-2 text-white font-extrabold text-xs uppercase tracking-widest px-6 py-3 rounded-lg"
            style={{ background: "#3a5c2f" }}
          >
            Browse Products
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
          {items.map((item) => {
            const inStock  = item.inStock !== false;
            const discount = item.originalPrice
              ? Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)
              : 0;

            const weightLabel =
              item.unit === "pack"
                ? (item.packSize   ? `${item.packSize} pack(s)` : null)
                : (item.weightSize ? `${item.weightSize} kg`    : null);

            const rateLabel =
              item.unit === "pack"
                ? (item.pricePerPack ? `₹${item.pricePerPack}/pack` : null)
                : (item.pricePerKg   ? `₹${item.pricePerKg}/kg`    : null);

            return (
              <Link to={`/products/${item.productId}`} key={item.productId} className="group block h-full">
                <div
                  className={`bg-white overflow-hidden transition-all duration-300 h-full flex flex-col
                    ${inStock ? "hover:shadow-2xl hover:-translate-y-1" : "opacity-75"}`}
                  style={{
                    borderRadius: "14px",
                    border: "1.5px solid #e0e0cc",
                    boxShadow: "0 2px 12px 0 rgba(60,80,30,0.07)",
                  }}
                >
                  {/* ── IMAGE ── */}
                  <div className="relative flex-shrink-0 overflow-hidden" style={{ aspectRatio: "3 / 2", borderRadius: "12px 12px 0 0" }}>
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-50 flex items-center justify-center text-5xl sm:text-7xl">🌱</div>
                    )}

                    {/* Sale badge – top-left */}
                    {discount > 0 && (
                      <div
                        className="absolute top-0 left-0 text-white text-[10px] sm:text-xs font-bold px-3 sm:px-4 py-1 sm:py-1.5 z-10 tracking-wider"
                        style={{ background: "#3a5c2f", borderRadius: "12px 0 14px 0" }}
                      >
                        Sale
                      </div>
                    )}

                    {/* 3-dot menu – top-right */}
                    <div
                      className="absolute top-2 right-2 z-20"
                      ref={openMenuId === item.productId ? menuRef : null}
                      onClick={(e) => e.preventDefault()}
                    >
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          setOpenMenuId(openMenuId === item.productId ? null : item.productId);
                        }}
                        className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/90 backdrop-blur-sm shadow flex items-center justify-center hover:scale-110 transition"
                        aria-label="Options"
                      >
                        <FiMoreVertical className="text-xs sm:text-sm text-gray-700" />
                      </button>

                      {openMenuId === item.productId && (
                        <div
                          className="absolute top-full right-0 mt-1.5 bg-white rounded-lg shadow-lg border border-gray-100 overflow-hidden w-44"
                        >
                          <button
                            onClick={(e) => {
                              setOpenMenuId(null);
                              handleRemove(e, item.productId, item.name);
                            }}
                            className="w-full flex items-center gap-2 px-2 py-2 text-[10px] font-semibold text-red-600 hover:bg-red-50 transition text-left"
                          >
                            <FiTrash2 className="text-[12px] flex-shrink-0" />
                            Remove from collection
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Weight pill – bottom-right */}
                    {weightLabel && (
                      <div
                        className="absolute bottom-2 right-2 text-white text-[9px] sm:text-[11px] font-bold px-2 sm:px-3 py-0.5 sm:py-1 z-10"
                        style={{ background: "rgba(0,0,0,0.55)", borderRadius: "999px", backdropFilter: "blur(4px)" }}
                      >
                        {weightLabel}
                      </div>
                    )}

                    {!inStock && (
                      <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
                        <span className="text-xs font-semibold text-gray-700 bg-white px-3 py-1 rounded-full shadow">
                          Out of stock
                        </span>
                      </div>
                    )}
                  </div>

                  {/* ── CONTENT – center-aligned, kasutam spacing ── */}
                  <div className="px-3 pt-3 pb-3 sm:px-4 sm:pt-4 sm:pb-4 flex flex-col flex-1 items-center text-center gap-1 sm:gap-1.5">

                    <StarRating rating={item.rating || 4.8} reviews={item.reviews || 120} />

                    <h3
                      className="font-bold text-gray-900 text-[11px] sm:text-sm uppercase leading-snug tracking-wide group-hover:text-green-700 transition-colors w-full mt-0.5 sm:mt-1"
                      style={{
                        display: "-webkit-box",
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden",
                        minHeight: "2.6em",
                      }}
                    >
                      {item.name}
                    </h3>

                    <div className="flex items-center justify-center gap-1.5 sm:gap-2 flex-wrap w-full mt-1">
                      {item.originalPrice && (
                        <span className="text-[11px] sm:text-sm text-gray-400 line-through font-medium">
                          ₹{item.originalPrice}/-
                        </span>
                      )}
                      <span className="text-sm sm:text-base font-extrabold text-gray-900">
                        ₹{item.price}/-
                      </span>
                      {discount > 0 && (
                        <span
                          className="text-[9px] sm:text-[11px] font-extrabold text-white px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap"
                          style={{ background: "#4a6e30" }}
                        >
                          -{discount}% OFF
                        </span>
                      )}
                    </div>

                    <div className="flex items-center justify-center gap-1.5 flex-wrap w-full">
                      {rateLabel && (
                        <p className="text-[9px] sm:text-[10px] text-gray-400 whitespace-nowrap">{rateLabel}</p>
                      )}
                      <span
                        className={`text-[9px] sm:text-[10px] font-semibold px-1.5 py-0.5 rounded-full whitespace-nowrap
                          ${inStock ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}
                      >
                        {inStock ? "In Stock" : "Out of Stock"}
                      </span>
                    </div>

                    {/* MOVE TO CART – full width, flat dark-green, kasutam style */}
                    <button
                      onClick={(e) => handleMoveToCart(e, item)}
                      disabled={!inStock}
                      className="mt-auto w-full flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 font-extrabold text-[10px] sm:text-xs uppercase tracking-widest transition-all duration-200 mt-2 sm:mt-3"
                      style={{
                        borderRadius: "8px",
                        background: inStock ? "#3a5c2f" : "#d1d5db",
                        color: inStock ? "#fff" : "#9ca3af",
                        cursor: inStock ? "pointer" : "not-allowed",
                        letterSpacing: "0.12em",
                      }}
                    >
                      <FiShoppingCart className="text-xs sm:text-sm flex-shrink-0" />
                      {inStock ? "Add to Cart" : "Out of Stock"}
                    </button>

                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}